// CRITICAL: Dengue (Qdenga®▼) must be administered by SUBCUTANEOUS
// injection only. It must NOT be given intramuscularly, intravenously, or
// intradermally. This is reflected in the brand administrationNotes,
// route ('SC'), and patient advice. Black triangle product.
// requiresYellowCardDiscussion: true. Seropositivity is enforced at
// basket selection and confirmed again at Tier 2 entry by the runner —
// no seropositivity exclusion check is added here as it is already a
// hard gate upstream. Covers both children (4–17 years) and adults with
// the same 0.5 mL dose. Two doses, 3 months apart.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.DENGUE,
  displayName: "Dengue (Qdenga\u00ae\u25bc)",
  serviceType: "administration",
  minimumAgeYears: 4,
  indicationSummary:
    "Seropositive individuals aged 4 years and over travelling to a dengue-risk area or where there is an ongoing dengue outbreak, as identified by NaTHNaC assessment. Documented prior dengue infection (seropositive status) is a mandatory prerequisite \u2014 confirmed before this pathway was opened.",

  brands: [
    {
      brandId: "qdenga",
      brandName: "Qdenga\u00ae\u25bc",
      minimumAgeYears: 4,
      route: "SC",
      doseDescription: "0.5 mL per dose (reconstituted)",
      administrationNotes:
        "Reconstitute the vaccine immediately before use per the manufacturer's SmPC. The reconstituted solution should be clear and colourless to slightly yellow \u2014 inspect before use; do not administer if discoloured or if particulate matter is present. Administer by SUBCUTANEOUS injection only, preferably into the deltoid region of the upper arm. DO NOT administer by intramuscular, intravenous, or intradermal injection. Subcutaneous route is mandatory for this product.",
    },
  ],

  schedules: [
    {
      scheduleId: "dengue-two-dose",
      label: "Two-dose schedule (day 0 and 3 months after dose 1)",
      offLabel: false,
      minimumAgeYears: 4,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 first dose (subcutaneous)",
        },
        {
          doseNumber: 2,
          label: "3 months after dose 1 (subcutaneous)",
          intervalFromDose1Days: 90,
        },
      ],
      completionNotes:
        "Both doses must be given by subcutaneous injection. The patient must return for the second dose at 3 months after the first dose. Not all recipients are fully protected \u2014 mosquito-bite avoidance remains essential.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "dengue-ex-under-4",
      question: "Is the patient under 4 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Qdenga\u00ae\u25bc is not licensed for patients under 4 years of age. Do not proceed.",
      referralMessage: "Refer to the patient's GP or a paediatric travel health specialist.",
    },
    {
      checkId: "dengue-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to the active substance or any excipient of Qdenga\u00ae\u25bc, or a previous severe allergic reaction to any dengue vaccine?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Qdenga\u00ae\u25bc is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "dengue-ex-hiv-immunosuppressed",
      question:
        "Does the patient have symptomatic HIV infection, or asymptomatic HIV with impaired immune function, a congenital immune deficiency, or acquired immune deficiency?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Qdenga\u00ae\u25bc is a live attenuated vaccine and is contraindicated in patients with symptomatic HIV, asymptomatic HIV with impaired immune function, congenital immune deficiency, or acquired immune deficiency. Do not proceed.",
      referralMessage: "Refer to the patient's GP or infectious disease specialist for advice on dengue prevention.",
    },
    {
      checkId: "dengue-ex-high-dose-steroids",
      question:
        "Has the patient been receiving high-dose systemic corticosteroids (20 mg/day or more of prednisolone, or equivalent, or 2 mg/kg/day or more in children) within the last 4 weeks?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Qdenga\u00ae\u25bc is contraindicated in patients who have received high-dose systemic corticosteroids within the last 4 weeks. Do not proceed.",
      referralMessage:
        "Refer to the patient's GP. Consider deferring vaccination until at least 4 weeks after high-dose corticosteroids have been stopped.",
    },
    {
      checkId: "dengue-ex-immunosuppressants",
      question:
        "Is the patient receiving, or has the patient recently received, any of the following? Biological immunosuppressive agents within the last 12 months; non-biological immunosuppressants within the last 6 months; oral immune-modulating agents within the last 3 months; or high-dose corticosteroids within the last 3 months.",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Qdenga\u00ae\u25bc is contraindicated in patients receiving or recently receiving the immunosuppressive agents listed. Do not proceed.",
      referralMessage:
        "Refer to the patient's GP or specialist for advice on dengue prevention and the appropriate timing of vaccination.",
    },
    {
      checkId: "dengue-ex-pregnant-breastfeeding",
      question: "Is the patient pregnant or breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Qdenga\u00ae\u25bc is contraindicated in pregnancy and breastfeeding. Do not proceed.",
      referralMessage: "Refer to the patient's GP or obstetrician for advice on dengue prevention during pregnancy.",
    },
    {
      checkId: "dengue-ex-live-vaccine-recent",
      question:
        "Has the patient received another live vaccine within the last 4 weeks, and is it not planned to give both vaccines on the same day at separate sites today?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Qdenga\u00ae\u25bc is a live vaccine. It must be given on the same day as other live vaccines (at separate sites) or at least 4 weeks apart. The minimum interval has not elapsed since the other live vaccine was given. Defer until the 4-week interval has passed.",
      referralMessage: "Rebook the appointment once the 4-week interval from the previous live vaccine has elapsed.",
    },
  ],

  cautions: [
    {
      checkId: "dengue-caut-immunosuppressed-mild",
      question:
        "Is the patient immunosuppressed to a degree that does not meet the exclusion criteria above (e.g. mild or stable immunosuppression not covered by the exclusion questions)?",
      cautionMessage:
        "Mild or stable immunosuppression may reduce the immune response to Qdenga\u00ae\u25bc. Vaccination may proceed if none of the exclusion criteria are met. Counsel the patient that protection may be suboptimal and that mosquito-bite avoidance remains essential.",
    },
    {
      checkId: "dengue-caut-over-60",
      question: "Is the patient aged 60 or over?",
      cautionMessage:
        "Clinical data in patients aged 60 and over are limited. Apply clinical judgement. Continue to emphasise mosquito-bite avoidance regardless of vaccination.",
    },
    {
      checkId: "dengue-caut-syncope",
      question: "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  // Qdenga is a black triangle product under additional monitoring.
  requiresYellowCardDiscussion: true,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against dengue virus. Not all recipients are fully protected \u2014 mosquito-bite avoidance remains essential throughout your trip.",
    "You must return for your second dose 3 months after today's dose. Both doses are needed for the best protection.",
    "Dengue is transmitted by Aedes mosquitoes, which bite during the day as well as at dawn and dusk. Apply insect repellent containing DEET to exposed skin throughout the day, wear long-sleeved clothing and long trousers, and use permethrin-treated clothing and bed nets.",
    "If you develop a sudden high fever with severe headache, pain behind the eyes, muscle and joint pain, rash, or nausea during or after travel in a dengue-risk area, seek urgent medical attention and tell the clinician of your travel history and dengue vaccination.",
    "Please report any suspected side effects to the MHRA via the Yellow Card scheme at yellowcard.mhra.gov.uk or by calling 0800 731 6789. Qdenga\u00ae\u25bc is a product under additional monitoring.",
  ],
});
